import { normalizeViewResponse } from '@services/feedView';
import { mockFeedKey } from './mockFeedKey';

// A real POST /feed/view response, captured 2026-08-20 and kept verbatim — including the fields
// nothing on this page RENDERS yet (`resolved_dag_sha`, and `feed_name`, which the page still has
// no server-confirmed use for), so this stays a faithful record of the wire shape rather than a
// tidied-up version of it. Per-input `mode` / `src_knowable_at` and the per-row
// `members_present` / `members_expected` / `members_missing` are normalized now and reach the UI
// layer; they are carried through to the row object and are simply not drawn in the grid yet.
//
// It also settles three of the normalizer's guesses in src/services/feedView.js: rows arrive under
// `rows`, the timestamp is `knowable_at` (never `utc_knowable_at`), and `inputs` is an object keyed
// by member name — with no per-input feed id, so the key *is* the member's identity.
//
// Development only. Reached from the localhost-gated control on the Feed Viewer form; nothing
// imports it in a deployed path.
export const MOCK_FEED_VIEW_RESPONSE = {
  feed_id: 'fundamental_earnings_screen_v1',
  feed_name: 'fundamental_earnings_screen_v1',
  resolved_dag_sha: '2843d6b8f6a1436f4ae6f7dfa7db223bc2d5e5333ab204d04f4b8ee9c15c8430',
  row_count: 3,
  rows: [
    {
      company_id: 299,
      knowable_at: '2025-10-31T06:01:04+00:00',
      score: null,
      inputs: {
        competitive_landscape_shift: {
          value: -0.6,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2025-10-31T06:01:04',
        },
        earnings_growth_strategy: {
          value: 0.75,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2025-10-31T06:01:04',
        },
        ebit_trend: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        forward_consensus_estimates: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        forward_ebit_margin_direction: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        input_cost_trend: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        investor_concerns: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        macro_revenue_correlation: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        pe_vs_3yr_average: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        recent_quarter_segment_performance: {
          value: null,
          status: 'missing',
          mode: 'occasion',
          src_knowable_at: null,
        },
      },
      members_present: 2,
      members_expected: 10,
      members_missing: [
        'ebit_trend',
        'forward_consensus_estimates',
        'forward_ebit_margin_direction',
        'input_cost_trend',
        'investor_concerns',
        'macro_revenue_correlation',
        'pe_vs_3yr_average',
        'recent_quarter_segment_performance',
      ],
    },
    {
      company_id: 299,
      knowable_at: '2026-01-30T07:01:04+00:00',
      score: null,
      inputs: {
        competitive_landscape_shift: {
          value: -0.3,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-01-30T07:01:04',
        },
        earnings_growth_strategy: {
          value: 0.85,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-01-30T07:01:04',
        },
        ebit_trend: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        forward_consensus_estimates: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        forward_ebit_margin_direction: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        input_cost_trend: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        investor_concerns: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        macro_revenue_correlation: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        pe_vs_3yr_average: { value: null, status: 'missing', mode: 'occasion', src_knowable_at: null },
        recent_quarter_segment_performance: {
          value: null,
          status: 'missing',
          mode: 'occasion',
          src_knowable_at: null,
        },
      },
      members_present: 2,
      members_expected: 10,
      members_missing: [
        'ebit_trend',
        'forward_consensus_estimates',
        'forward_ebit_margin_direction',
        'input_cost_trend',
        'investor_concerns',
        'macro_revenue_correlation',
        'pe_vs_3yr_average',
        'recent_quarter_segment_performance',
      ],
    },
    {
      company_id: 299,
      knowable_at: '2026-04-30T09:00:00+00:00',
      score: 2.2,
      inputs: {
        competitive_landscape_shift: {
          value: -0.6,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        earnings_growth_strategy: {
          value: 0.8,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        ebit_trend: { value: 0.8, status: 'present', mode: 'occasion', src_knowable_at: '2026-04-30T09:00:00' },
        forward_consensus_estimates: {
          value: 0.6,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        forward_ebit_margin_direction: {
          value: 0.2,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        input_cost_trend: {
          value: -0.25,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        investor_concerns: {
          value: -0.2,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        macro_revenue_correlation: {
          value: -0.5,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
        pe_vs_3yr_average: { value: 0.0, status: 'present', mode: 'occasion', src_knowable_at: '2026-04-30T09:00:00' },
        recent_quarter_segment_performance: {
          value: 0.6,
          status: 'present',
          mode: 'occasion',
          src_knowable_at: '2026-04-30T09:00:00',
        },
      },
      members_present: 10,
      members_expected: 10,
      members_missing: [],
    },
  ],
};

// SYNTHETIC — not a capture. Written by hand to exercise the paths PR #431 adds that the real
// capture above predates: a single-input score feed (no `inputs`, so no markers), the score spec's
// declared payload columns, the point-in-time enrichment (ticker, company_name, sector, market_cap,
// the is_member_of_<index> flags), and `available_columns`. Replace it with a real capture when one
// is available; until then, treat its field values as illustrative and its field *names* as taken
// from the handoff, not from the wire.
//
// `available_columns` is here because no captured fixture in this repo carries it, so the entire
// universe-driven column path — the one that decides what read #2 of a feed is allowed to ask for —
// had only ever run against objects built inside test files. Running it against something shaped
// like a whole response is the point; it is not evidence that a server sends this list.
export const MOCK_SINGLE_INPUT_RESPONSE = {
  feed_id: 'score_spec_ebit_trend_v1',
  feed_name: 'score_spec_ebit_trend_v1',
  row_count: 3,
  rows: [
    {
      company_id: 15,
      ticker: 'AOS',
      company_name: 'A. O. Smith Corporation',
      utc_knowable_at: '2026-01-31T00:01:03+00:00',
      score: 0.8,
      sector: 'Industrials',
      market_cap: 11321247073,
      is_member_of_sp500: true,
      is_member_of_djia30: false,
      explanation: 'EBIT expanded for a third consecutive quarter, led by pricing rather than volume.',
      thinking: 'Compared segment margins year over year, then checked whether pricing or mix drove the gain.',
    },
    {
      company_id: 299,
      ticker: 'CAT',
      company_name: 'Caterpillar Inc.',
      utc_knowable_at: '2026-02-14T00:01:03+00:00',
      score: -0.35,
      sector: 'Industrials',
      market_cap: 168004120000,
      is_member_of_sp500: true,
      is_member_of_djia30: true,
      explanation: 'EBIT fell against a tough comparison; dealer inventories absorbed most of the shortfall.',
      thinking: 'Weighed the comparison base against the reported decline before scoring it negative.',
    },
    {
      company_id: 512,
      ticker: 'AWK',
      company_name: 'American Water Works Company, Inc.',
      utc_knowable_at: '2026-02-20T00:01:03+00:00',
      score: null,
      sector: 'Utilities',
      market_cap: null,
      is_member_of_sp500: true,
      is_member_of_djia30: false,
      explanation: 'No comparable EBIT disclosure in the filing for this occasion.',
    },
  ],
  // The feed's whole universe: the floor, then everything `columns` may name on top of it. Wider
  // than what these rows carry, on purpose and in two directions that matter.
  //
  // `is_member_of_nasdaq100` appears here and on no row. That is the case the universe exists for:
  // harvesting flag names from rows can only ever find the ones some company happens to be in, so a
  // read that learned its names that way can never ask about an index this sample misses — and a
  // name that is never asked for is a column that never arrives, which is the closed loop.
  //
  // `explanation` and `thinking` are the score spec's declared payload, and are unguessable by
  // construction — no generic first read could name them, which is why read #1 guesses the three
  // columns every feed plausibly has and read #2 asks for this list instead.
  available_columns: [
    'utc_knowable_at',
    'ticker',
    'company_id',
    'score',
    'sector',
    'market_cap',
    'company_name',
    'is_member_of_sp500',
    'is_member_of_djia30',
    'is_member_of_nasdaq100',
    'explanation',
    'thinking',
  ],
};

// SYNTHETIC — not a capture, and the only fixture here that can show the Prev score and Change
// columns a number.
//
// Neither of the two above can, and neither should be edited to. The captured response is three
// occasions of ONE company with scores [null, null, 2.2], so under the previous-occasion rule its
// two later rows have a predecessor that scored nothing and its Change column is empty throughout —
// which is a real and important case, and exactly what a combined feed looks like, but it is not a
// demonstration. The single-input response is three DIFFERENT companies at one occasion each, so
// nothing in it has a predecessor at all. It is also pinned by mockFeed.test.js, and widening it
// would rewrite assertions that are about something else.
//
// So: one company, three scored occasions, one rise and one fall. Deliberately a score feed rather
// than a screen — no `inputs`, so the score column washes and the derived pair washes with it.
//
// Plus one UNSCORED occasion, at the head of that company's series. It is there for the Movement
// column, which needs a gap to show what a gap looks like, and it buys a second demonstration on the
// way: the row after it has a predecessor that exists and scored nothing, which is the one Prev
// score blank the copy distinguishes from "no earlier occasion came back". At the head rather than
// mid-series on purpose — an unscored occasion between the rise and the fall would empty the Change
// column of the very numbers this fixture exists to show, since a predecessor is the previous
// OCCASION, not the previous scored one.
export const MOCK_HISTORY_RESPONSE = {
  feed_id: 'score_spec_margin_trend_v1',
  feed_name: 'score_spec_margin_trend_v1',
  row_count: 5,
  rows: [
    {
      company_id: 15,
      ticker: 'AOS',
      company_name: 'A. O. Smith Corporation',
      utc_knowable_at: '2025-05-30T00:01:03+00:00',
      score: null,
      sector: 'Industrials',
      market_cap: 10980000000,
      is_member_of_sp500: true,
      explanation: 'No qualifying filing in the window — the occasion is kept so the gap is visible.',
    },
    {
      company_id: 15,
      ticker: 'AOS',
      company_name: 'A. O. Smith Corporation',
      utc_knowable_at: '2025-08-29T00:01:03+00:00',
      score: 1.85,
      sector: 'Industrials',
      market_cap: 11321247073,
      is_member_of_sp500: true,
      explanation: 'Gross margin widened on a mix shift toward aftermarket parts.',
    },
    {
      company_id: 15,
      ticker: 'AOS',
      company_name: 'A. O. Smith Corporation',
      utc_knowable_at: '2025-11-28T00:01:03+00:00',
      score: 2.2,
      sector: 'Industrials',
      market_cap: 11890220000,
      is_member_of_sp500: true,
      explanation: 'A third consecutive quarter of expansion, this time with volume behind it.',
    },
    {
      company_id: 15,
      ticker: 'AOS',
      company_name: 'A. O. Smith Corporation',
      utc_knowable_at: '2026-02-27T00:01:03+00:00',
      score: 1.9,
      sector: 'Industrials',
      market_cap: 11402990000,
      is_member_of_sp500: true,
      explanation: 'Margin gave back part of the gain as input costs caught up.',
    },
    // A company with one occasion, sitting beside one with three — so the sample shows what the
    // oldest row of a company looks like without the whole column being that case.
    {
      company_id: 512,
      ticker: 'AWK',
      company_name: 'American Water Works Company, Inc.',
      utc_knowable_at: '2026-02-27T00:01:03+00:00',
      score: -0.4,
      sector: 'Utilities',
      market_cap: null,
      is_member_of_sp500: true,
      explanation: 'Rate-case timing compressed the reported margin.',
    },
  ],
  available_columns: [
    'utc_knowable_at',
    'ticker',
    'company_id',
    'score',
    'sector',
    'market_cap',
    'company_name',
    'is_member_of_sp500',
    'explanation',
  ],
};

const RESPONSES = {
  multi: MOCK_FEED_VIEW_RESPONSE,
  single: MOCK_SINGLE_INPUT_RESPONSE,
  history: MOCK_HISTORY_RESPONSE,
};

export const MOCK_KINDS = { MULTI: 'multi', SINGLE: 'single', HISTORY: 'history' };

// The order the form's kind button cycles through. A list rather than a pair of ternaries, so adding
// a fourth fixture is one more entry here instead of a rewrite of the control.
export const MOCK_KIND_ORDER = [MOCK_KINDS.MULTI, MOCK_KINDS.SINGLE, MOCK_KINDS.HISTORY];

export const MOCK_KIND_LABELS = {
  [MOCK_KINDS.MULTI]: 'Screen feed',
  [MOCK_KINDS.SINGLE]: 'Score feed',
  [MOCK_KINDS.HISTORY]: 'Score feed, several occasions',
};

export const nextMockKind = (kind) => {
  const at = MOCK_KIND_ORDER.indexOf(kind);
  return MOCK_KIND_ORDER[(at + 1) % MOCK_KIND_ORDER.length];
};

// Same normalization a live response gets, plus an isMock flag so the table can say out loud that
// these numbers didn't come from the API.
export const loadMockFeedResult = ({ slim = false, kind = MOCK_KINDS.MULTI } = {}) => {
  const response = RESPONSES[kind] || MOCK_FEED_VIEW_RESPONSE;
  return {
    ...normalizeViewResponse(response, { slim }),
    isMock: true,
    isSynthetic: response !== MOCK_FEED_VIEW_RESPONSE,
    // The fixture's DISPLAY key, not the wire id it borrowed. `response.feed_id` names a real feed —
    // filing the analyst's column choices under it would put a fixture's state in a live feed's
    // namespace, which is the collision the `mock:` prefix exists to prevent.
    feedId: mockFeedKey(kind),
  };
};
